import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useUpdateAccountStatus } from '~/hooks/apis/loyalty/useUpdateAccountStatus';

import type { LoyaltyAccountStatus } from '~/services/LuxLoyalty';

/** Mirrors the server's limit on the status-change reason. */
const REASON_MAX_LENGTH = 500;

interface Props {
  open: boolean;
  onClose: () => void;
  customerId: string;
  currentStatus: LoyaltyAccountStatus;
  /** Called after the mutation succeeds so the parent can refresh its account view. */
  onSuccess?: () => void;
}

/** Closes an active account or re-opens a closed one, collecting the mandatory reason. */
export default function LuxLoyaltyAccountStatusDialog({ open, onClose, customerId, currentStatus, onSuccess }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [reason, setReason] = useState('');
  const isClosing = currentStatus === 'active';
  const targetStatus: LoyaltyAccountStatus = isClosing ? 'closed' : 'active';

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  const updateStatusMutation = useUpdateAccountStatus({
    onSuccess: ({ result }) => {
      if (result.changed) {
        enqueueSnackbar(isClosing ? 'Account closed' : 'Account re-opened', { variant: 'success' });
      } else {
        enqueueSnackbar(`Account was already ${result.accountStatus === 'closed' ? 'closed' : 'active'}`, {
          variant: 'info',
        });
      }
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to ${isClosing ? 'close' : 're-open'} account: ${error.message}`, { variant: 'error' });
    },
  });

  const trimmedReason = reason.trim();
  const canSubmit = trimmedReason.length > 0 && !updateStatusMutation.isPending;

  return (
    <Dialog open={open} onClose={updateStatusMutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isClosing ? 'Close account' : 'Re-open account'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            {isClosing
              ? 'The account will show as closed to the customer, and its points and benefits can no longer be used. Only an admin can re-open it.'
              : 'The customer will be able to use the account, its points and benefits again.'}
          </Typography>
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            fullWidth
            multiline
            minRows={3}
            placeholder={`Why is this account being ${isClosing ? 'closed' : 're-opened'}?`}
            slotProps={{ htmlInput: { maxLength: REASON_MAX_LENGTH } }}
            helperText={`${reason.length}/${REASON_MAX_LENGTH}`}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={updateStatusMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isClosing ? 'error' : 'primary'}
          onClick={() => updateStatusMutation.mutate({ customerId, status: targetStatus, reason: trimmedReason })}
          disabled={!canSubmit}
          startIcon={updateStatusMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isClosing ? 'Close account' : 'Re-open account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
